import type { PillTone } from '@/components/kit/Pill';
import type { StageTone } from '@/components/kit/Board';
import type { DealStage, LeadStatus, Readiness, RegStatus } from '@/api/m3-types';
import { withPlural } from '@/lib/format';

/**
 * Подписи состояний M3 — одно место для экранов CRM, регистраций
 * и карточек M2. Названия и тона — как в макете (LST, STAGES, PART, ready()).
 */

export const LEAD_STATUS: Record<LeadStatus, { label: string; tone: PillTone }> = {
  new: { label: 'Новая', tone: 'info' },
  in_progress: { label: 'В работе', tone: 'warn' },
  converted: { label: 'Сконвертирована', tone: 'ok' },
  rejected: { label: 'Отказ', tone: 'mut' },
  duplicate: { label: 'Дубль', tone: 'mut' },
};

/** Стадии сделки: роль этапа задаёт цвет точки (кит 2.14) */
export const STAGES: { key: DealStage; title: string; tone: StageTone; auto: boolean; why?: string }[] = [
  { key: 'new', title: 'Новая', tone: 'info', auto: false },
  { key: 'offer', title: 'КП отправлено', tone: 'warn', auto: false },
  { key: 'registered', title: 'Регистрация получена', tone: 'neutral', auto: true, why: 'наступает сама, когда в сделке есть регистрация' },
  { key: 'prepaid', title: 'Предоплата получена', tone: 'info', auto: true, why: 'наступает сама с первым платежом по номеру' },
  { key: 'won', title: 'Выиграно', tone: 'ok', auto: true, why: 'наступает сама, когда все номера оплачены полностью' },
];

export const LOST_STAGE = { key: 'lost' as const, title: 'Проиграна', tone: 'neutral' as StageTone, auto: false };

export const stageTitle = (s: DealStage) => (s === 'lost' ? LOST_STAGE.title : STAGES.find((x) => x.key === s)!.title);
export const stageTone = (s: DealStage): StageTone => (s === 'lost' ? LOST_STAGE.tone : STAGES.find((x) => x.key === s)!.tone);

export const REG_STATUS: Record<RegStatus, { label: string; tone: PillTone }> = {
  active: { label: 'Участвует', tone: 'ok' },
  reserve: { label: 'Резерв', tone: 'info' },
  declined: { label: 'Отказ', tone: 'mut' },
  moved: { label: 'Перенесена', tone: 'mut' },
};

export const READINESS: Record<Readiness, { label: string; tone: PillTone }> = {
  not_started: { label: 'Не начата', tone: 'mut' },
  in_progress: { label: 'В работе', tone: 'warn' },
  ready: { label: 'Готова к допуску', tone: 'info' },
  admitted: { label: 'Допущена', tone: 'ok' },
};

/** «12 мин», «2 ч 5 мин», «3 дня» — fmtMin() в макете */
export function minutesText(m: number): string {
  if (m < 60) return `${m} мин`;
  if (m < 1440) return `${Math.floor(m / 60)} ч${m % 60 ? ` ${m % 60} мин` : ''}`;
  return withPlural(Math.floor(m / 1440), 'день', 'дня', 'дней');
}

export const daysText = (n: number) => withPlural(n, 'день', 'дня', 'дней');

/** «Без движения»: 0 — «сегодня», иначе дни */
export const idleText = (n: number) => (n ? daysText(n) : 'сегодня');

/** Инициалы для аватара: две первые буквы слов */
export const initialsOf = (name: string) =>
  name
    .replace(/[«»"]/g, '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w.charAt(0))
    .join('')
    .toUpperCase();

/** «Ковалёва М.» — имя ответственного на чипе */
export const shortUser = (fullName: string) => {
  const [a, b] = fullName.split(/\s+/);
  return b ? `${a} ${b.charAt(0)}.` : a;
};

/** «21.10 · 13:15» → две строки списка: дата и время поступления */
export function dateTime(iso: string): { date: string; time: string } {
  const d = new Date(iso);
  const p = (n: number) => String(n).padStart(2, '0');
  return { date: `${p(d.getDate())}.${p(d.getMonth() + 1)}`, time: `${p(d.getHours())}:${p(d.getMinutes())}` };
}

export const fullDateTime = (iso: string) => {
  const d = new Date(iso);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

export const nums = (n: number) => withPlural(n, 'номер', 'номера', 'номеров');
export const deals = (n: number) => withPlural(n, 'сделка', 'сделки', 'сделок');

/** «Иркутск 14–15.11» — так же, как сервер подписывает мероприятие в ленте */
export function eventLabel(e: { city: string; dateFrom: string; dateTo: string }): string {
  const [, m1, d1] = e.dateFrom.split('-');
  const [, m2, d2] = e.dateTo.split('-');
  if (e.dateFrom === e.dateTo) return `${e.city} ${d1}.${m1}`;
  if (m1 === m2) return `${e.city} ${Number(d1)}–${d2}.${m2}`;
  return `${e.city} ${d1}.${m1}–${d2}.${m2}`;
}
